import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import ModalContext from 'contexts/ModalContext'
import JourneyV2DetailsModal from 'components/Flights/V2/FareFamily/JourneyV2DetailsModal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDesktop from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidgetDesktop'
import useSearchFlights from 'hooks/useSearchFlights'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { FlightSegment, FlightSortOptions, FlightsFareTypes, fareClassToUrlMap, FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { INTL_DAY_MONTH_NAME, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { resetFlightsTimer, hasFlightSessionExpired } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import DrawerFlightCard from 'tripPlanner/components/Chat/Flights/DrawerFlightCard'
import Group from 'components/utils/Group'
import StepperProgressSteps from 'LuxKit/Steppers/StepperProgressSteps'
import StepperProgressStep from 'LuxKit/Steppers/StepperProgressStep'
import type { StepClickHandler } from 'LuxKit/Steppers/StepperProgressStep'
import FlightsLoadingTopBar from 'components/Flights/V2/FlightsLoadingTopBar'
import LoadingAnimationBox from 'components/Common/LoadingAnimationBox/LoadingAnimationBox'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { optionToJourney } from './chatFlightTypes'
import type { FlightUIData, FlightOption } from './chatFlightTypes'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import ChatFlightResultsHeader from './Flights/ChatFlightResultsHeader'
import useChatFlightResultsSorting from './Flights/useChatFlightResultsSorting'
import { getSegmentKey, getSegmentKeys, getSearchItinerary } from './Flights/flightSegmentUtils'
import { buildFlightContext } from './Flights/buildFlightContext'
import { buildMultiCityFlightMessage } from './Flights/multiCityFlightMessage'
import { useTripFlightFilters } from './Flights/useTripFlightFilters'
import TripFlightFiltersPanel from './Flights/TripFlightFiltersPanel'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import type { TripItineraryData } from 'tripPlanner/api/types'
export interface FlightSearchDrawerComponentProps {
  isOpen: boolean
  onClose: () => void
  originAirportCode?: string
  originAirportName?: string
  destinationAirportCode?: string
  destinationAirportName?: string
  departureDate?: string
  returnDate?: string
  indicativeSearchMonth?: string
  itinerary?: FlightSearchParams['itinerary']
  fareType?: string
  fareClass?: string
  occupants?: App.Occupants
  passengers?: number
  autoSearch?: boolean
  showMultiCity?: boolean
  onSendMessage?: (message: string) => void
  isStreaming?: boolean
  tripStopId?: number | null
}

interface Props {
  data: FlightUIData
  onSendMessage?: (message: string) => void
  isStreaming?: boolean
  /** Trip itinerary for indicative calendar focus; threaded from Message actions. */
  itineraryData?: TripItineraryData
  FlightSearchDrawerComponent?: ComponentType<FlightSearchDrawerComponentProps>
}

const SearchWrapper = styled.div`
  padding: ${rem(12)} ${rem(16)};
  width: 100%;
  box-sizing: border-box;
`

const FlightSearchWidgetWrapper = styled.div`
  width: 100%;

  ${mediaQueryUp.tablet} {
    form {
      grid-template-areas:
        "location-input location-input"
        "date-input actions";
      grid-template-columns: minmax(0, 1fr) ${rem(112)};
    }

    form > .location-input-area,
    form > .date-input-area {
      min-width: 0;
    }
  }
`

const ContentWrapper = styled.div`
  padding: ${rem(16)};
  width: 100%;
  box-sizing: border-box;
`

const FlightResultsViewport = styled.div<{ $maxHeight?: number; $isScrollable: boolean }>`
  position: relative;
  overflow-x: hidden;
  overflow-y: ${({ $isScrollable }) => $isScrollable ? 'auto' : 'hidden'};
  max-height: ${({ $maxHeight }) => $maxHeight ? rem($maxHeight) : 'none'};
  overscroll-behavior: contain;
`

const FlightResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const FLIGHT_RESULTS_GAP = 12
const MAX_VISIBLE_FLIGHT_COUNT = 6
const FLIGHT_RESULTS_SCROLL_PEEK_HEIGHT = 36
const FLIGHT_RESULTS_FALLBACK_CARD_HEIGHT = 112

const EMPTY_SEARCH_PARAMS: FlightSearchParams = {
  itinerary: [{ departureAirport: '', arrivalAirport: '', departureDate: '' }],
  occupancies: [{ adults: 1 }],
  fareClass: 'economy',
}

const MAX_TIMEOUT_MS = 2147483647

const EMPTY_FLIGHT_OPTIONS: Array<FlightOption> = []

const EMPTY_JOURNEYS: Array<App.JourneyV2> = []

// Cap how many pre-streamed options we surface per multi-city leg. The backend
// may return 100+ options per segment; we only ever show a shortlist.
const MAX_OPTIONS_PER_SEGMENT = 10

function getInitialJourneyId(segmentKey: string, optionIndex: number): string {
  return `${segmentKey}-${optionIndex}`
}

function areStringArraysEqual(left: Array<string>, right: Array<string>): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

function ChatFlightSearchContent({
  data,
  onSendMessage,
  isStreaming = false,
  itineraryData,
  FlightSearchDrawerComponent,
}: Props) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const canRequestLuxPlusFlightPricing = canRedeemLuxPlusBenefits && currentRegionCode === 'AU'

  const [searchParams, setSearchParams] = useState<FlightSearchParams | null>(null)
  const [searchVersion, setSearchVersion] = useState(0)
  const [activeSegmentKey, setActiveSegmentKey] = useState<string>(FlightSegment.DEPARTING)
  const [flightSort, setFlightSort] = useState<FlightSortOptions>(FlightSortOptions.Best)
  const [selectedFlights, setSelectedFlights] = useState<Record<string, App.JourneyV2>>({})
  const [selectedFareFamilies, setSelectedFareFamilies] = useState<Record<string, App.FlightFareFamily>>({})
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [flightCardHeight, setFlightCardHeight] = useState(0)
  const flightResultsListRef = useRef<HTMLDivElement | null>(null)

  const openDrawer = useCallback(() => setIsDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), [])

  const widgetState = useContext(FlightSearchWidgetStateContext)
  const { flights, occupants, fareType, fareCabin } = widgetState

  const isReturn = fareType?.value === FlightsFareTypes.RETURN
  const isMultiCity = fareType?.value === FlightsFareTypes.MULTI_CITY
  const defaultSegmentCount = isReturn ? 2 : Math.max(1, flights.length)
  const segments = useMemo<Array<string>>(() => getSegmentKeys(searchParams?.itinerary.length ?? defaultSegmentCount, isReturn), [defaultSegmentCount, isReturn, searchParams?.itinerary.length])
  const isReturningStep = activeSegmentKey === FlightSegment.RETURNING
  const hasSearched = searchParams !== null

  const outboundOptions = data.outboundOptions ?? EMPTY_FLIGHT_OPTIONS
  const inboundOptions = data.inboundOptions ?? EMPTY_FLIGHT_OPTIONS

  const initialDepartingJourneys = useMemo(() => {
    return outboundOptions.map((opt, i) =>
      optionToJourney(opt, `departing-${i}`, opt.currencyCode ?? data.currencyCode),
    )
  }, [outboundOptions, data.currencyCode])

  const initialReturningJourneys = useMemo(() => {
    return inboundOptions.map((opt, i) =>
      optionToJourney(opt, `returning-${i}`, opt.currencyCode ?? data.currencyCode),
    )
  }, [inboundOptions, data.currencyCode])

  // Every pre-streamed multi-city option per leg (journey + its optionRef),
  // built from the FULL option list. Compatibility filtering has to run against
  // all options — for a path-dependent itinerary the only valid next-leg option
  // can sit outside the first MAX_OPTIONS_PER_SEGMENT — so we cap for display
  // only after filtering (in unsortedDisplayJourneys), never here.
  const initialSegmentEntries = useMemo<Record<string, Array<{ journey: App.JourneyV2; optionRef: string }>>>(() => {
    const map: Record<string, Array<{ journey: App.JourneyV2; optionRef: string }>> = {}
    data.segments?.forEach((segment, index) => {
      const key = getSegmentKey(index, isReturn)
      map[key] = segment.options.map((opt, i) => ({
        journey: optionToJourney(opt, getInitialJourneyId(key, i), opt.currencyCode ?? data.currencyCode),
        optionRef: opt.optionRef,
      }))
    })
    return map
  }, [data.segments, data.currencyCode, isReturn])

  // Pre-streamed results keyed by segment. Multi-city delivers one option list
  // per leg in `data.segments`; legacy one-way/return reuse the departing /
  // returning option lists. The active segment drives which list is shown
  // before the user runs a fresh search.
  const initialJourneysBySegment = useMemo<Record<string, Array<App.JourneyV2>>>(() => {
    if (data.segments?.length) {
      const map: Record<string, Array<App.JourneyV2>> = {}
      for (const [key, entries] of Object.entries(initialSegmentEntries)) {
        map[key] = entries.map(entry => entry.journey)
      }
      return map
    }

    const map: Record<string, Array<App.JourneyV2>> = {}
    segments.forEach((key, index) => {
      map[key] = isReturn && index === 1 ? initialReturningJourneys : initialDepartingJourneys
    })
    return map
  }, [data.segments, initialSegmentEntries, segments, isReturn, initialDepartingJourneys, initialReturningJourneys])

  // Keyed by the ACTUAL rendered journey.id: optionToJourney can derive the id
  // from savedItemData.journeyId rather than the id we pass in, so keying by the
  // built journey keeps compatibility lookups by journey.id resolving correctly.
  const initialJourneyOptionRefsById = useMemo<Record<string, string>>(() => {
    const refsById: Record<string, string> = {}
    for (const entries of Object.values(initialSegmentEntries)) {
      for (const entry of entries) {
        refsById[entry.journey.id] = entry.optionRef
      }
    }
    return refsById
  }, [initialSegmentEntries])

  // activeSegmentKey defaults to DEPARTING, but multi-city segment keys are
  // `flight-N`; fall back to the first segment so the lookup resolves on the
  // initial render.
  const activeSegment = segments.includes(activeSegmentKey)
    ? activeSegmentKey
    : segments[0] ?? activeSegmentKey

  const { journeys: searchedJourneys, fetching, error, searchResultsTimestamp } = useSearchFlights(
    searchParams ?? EMPTY_SEARCH_PARAMS,
    {
      disabled: !searchParams,
      segments,
      activeSegmentKey: activeSegment,
      selectedFlights,
      selectedFareFamilies,
      isStandalone: canRequestLuxPlusFlightPricing,
    },
  )

  const departDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkinDate ? moment(flight.checkinDate).format(ISO_DATE_FORMAT) : undefined
  }, [flights])

  const returnDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkoutDate ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT) : undefined
  }, [flights])

  const currentSegmentIndex = useMemo(() => Math.max(0, segments.findIndex(s => s === activeSegment)), [segments, activeSegment])

  const resultsHeaderLabel = useMemo(() => {
    if (isMultiCity) {
      return intl.formatMessage({ id: 'xmce4KMu4_6CYz9hwEpo', defaultMessage: 'Flight {flightNumber}', description: 'Label for a multi-city flight segment with its sequence number' }, { flightNumber: currentSegmentIndex + 1 })
    }
    if (isReturningStep) {
      return intl.formatMessage({ id: '4p1QhxNAMtAdkUW9nReN', defaultMessage: 'Returning flight', description: 'Label for returning flight segment in trip planner drawer' })
    }
    return intl.formatMessage({ id: 'qJfCIiKntFNv9yNLHAHJ', defaultMessage: 'Departing flight', description: 'Label for departing flight segment in trip planner drawer' })
  }, [intl, isMultiCity, isReturningStep, currentSegmentIndex])

  const unsortedDisplayJourneys = useMemo<Array<App.JourneyV2>>(() => {
    if (hasSearched) return searchedJourneys

    const initialJourneys = initialJourneysBySegment[activeSegment] ?? EMPTY_JOURNEYS
    // Legacy one-way/return lists are already display-sized; show them as-is.
    if (!data.segments?.length) return initialJourneys

    // Multi-city pre-streamed legs hold the full option list. Filter to the
    // compatible options first, then cap to MAX_OPTIONS_PER_SEGMENT for display.
    if (!isMultiCity || !data.compatibleTransitions?.length) {
      return initialJourneys.slice(0, MAX_OPTIONS_PER_SEGMENT)
    }

    const selectedOptionRefs = segments
      .slice(0, currentSegmentIndex)
      .map(segment => selectedFlights[segment])
      .map(journey => journey ? initialJourneyOptionRefsById[journey.id] : undefined)
    if (selectedOptionRefs.some(ref => !ref)) return EMPTY_JOURNEYS
    if (selectedOptionRefs.length === 0) return initialJourneys.slice(0, MAX_OPTIONS_PER_SEGMENT)

    const selectedRefs = selectedOptionRefs.filter((ref): ref is string => !!ref)
    const compatibleOptionRefs = new Set(
      data.compatibleTransitions
        .filter(transition => areStringArraysEqual(transition.selectedOptionRefs, selectedRefs))
        .flatMap(transition => transition.nextOptions.map(option => option.optionRef)),
    )
    return initialJourneys
      .filter(journey => compatibleOptionRefs.has(initialJourneyOptionRefsById[journey.id]))
      .slice(0, MAX_OPTIONS_PER_SEGMENT)
  }, [
    activeSegment,
    currentSegmentIndex,
    data.compatibleTransitions,
    data.segments,
    hasSearched,
    initialJourneyOptionRefsById,
    initialJourneysBySegment,
    isMultiCity,
    searchedJourneys,
    segments,
    selectedFlights,
  ])

  const displayJourneys = useChatFlightResultsSorting({
    journeys: unsortedDisplayJourneys,
    activeSegmentKey: activeSegment,
    sort: flightSort,
    departDate,
    returnDate,
    selectedFlights,
  })

  // Filter the sorted, same-day-aware results. Options are built from
  // `displayJourneys` so they stay stable as the user narrows the selection;
  // filters reset whenever the search or active segment changes.
  const tripFlightFilters = useTripFlightFilters(displayJourneys, {
    resetKey: `${searchVersion}-${activeSegment}`,
  })
  const { visibleJourneys, resetAllFilters } = tripFlightFilters

  const handleSearch = useCallback(() => {
    const itinerary = getSearchItinerary(flights, fareType?.value)
    if (!itinerary) {
      return
    }

    const nextVersion = searchVersion + 1
    setSearchVersion(nextVersion)
    setSearchParams({
      itinerary,
      occupancies: [occupants],
      fareClass: fareCabin?.value ? fareClassToUrlMap[fareCabin.value] ?? 'economy' : 'economy',
      fareType: fareType?.value,
      key: nextVersion,
    })
    setActiveSegmentKey(getSegmentKey(0, fareType?.value === FlightsFareTypes.RETURN))
    setSelectedFlights({})
    setSelectedFareFamilies({})
  }, [flights, occupants, fareType, fareCabin, searchVersion])

  const initialExpiryTimestamp = data.searchExpiry ?? 0

  const searchExpiryTimestamp = useMemo(() => {
    if (!searchResultsTimestamp) return 0
    return moment(searchResultsTimestamp).add(FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, 'minutes').toDate().getTime()
  }, [searchResultsTimestamp])

  const expiryTimestamp = hasSearched ? searchExpiryTimestamp : initialExpiryTimestamp

  useEffect(() => {
    if (expiryTimestamp) {
      resetFlightsTimer(expiryTimestamp)
    }
  }, [expiryTimestamp])

  const handleSearchRef = useRef(handleSearch)
  handleSearchRef.current = handleSearch

  const isWidgetReady = !!flights[0]?.departureAirport?.airportCode &&
    !!flights[0]?.arrivalAirport?.airportCode &&
    !!flights[0]?.checkinDate &&
    (!isMultiCity || flights.every(flight => !!flight.departureAirport?.airportCode && !!flight.arrivalAirport?.airportCode && !!flight.checkinDate))

  useEffect(() => {
    if (fetching || !expiryTimestamp || !isWidgetReady) return
    // Multi-city results are streamed per-leg in `data.segments`; an expired
    // session must not auto-fire a live search that replaces them before the
    // user has acted. They can refresh manually via the Search button.
    if (isMultiCity && !hasSearched) return
    if (hasFlightSessionExpired()) {
      handleSearchRef.current()
      return
    }
    const remaining = expiryTimestamp - Date.now()
    if (remaining <= 0) {
      handleSearchRef.current()
      return
    }
    const timeout = setTimeout(() => { handleSearchRef.current() }, Math.min(remaining, MAX_TIMEOUT_MS))
    return () => clearTimeout(timeout)
  }, [fetching, expiryTimestamp, isWidgetReady, isMultiCity, hasSearched])

  const handleStepClick = useCallback<StepClickHandler>((stepIndex) => {
    setActiveSegmentKey(segments[stepIndex])
    // Keep the clicked step's own selection; only clear the legs after it (their
    // choices may depend on this leg, and the user is re-opening this step to
    // review/change it, not to lose it).
    const keysToRemove = segments.slice(stepIndex + 1)
    setSelectedFlights(prev => {
      const next = { ...prev }
      for (const key of keysToRemove) delete next[key]
      return next
    })
    setSelectedFareFamilies(prev => {
      const next = { ...prev }
      for (const key of keysToRemove) delete next[key]
      return next
    })
  }, [segments])

  const getStepSublabel = useCallback((journey: App.JourneyV2) => {
    const flight = journey.flightGroup.flights[0]
    const date = dateFormatter(flight.departureDateTime, INTL_DAY_MONTH_NAME)
    return `${date} · ${flight.departingTime}`
  }, [dateFormatter])

  const stepperItinerary = useMemo(() => {
    if (hasSearched && searchParams) return searchParams.itinerary
    if (data.segments?.length) {
      return data.segments.map((segment) => {
        const leg = segment.options[0]?.leg
        return {
          departureAirport: leg?.departureAirport ?? '',
          arrivalAirport: leg?.arrivalAirport ?? '',
          departureDate: segment.departureDate,
        }
      })
    }
    const firstOutbound = outboundOptions[0]?.leg
    if (!firstOutbound) return []
    const legs = [{ departureAirport: firstOutbound.departureAirport, arrivalAirport: firstOutbound.arrivalAirport, departureDate: firstOutbound.departureDate }]
    const firstInbound = inboundOptions[0]?.leg
    if (isReturn && firstInbound) {
      legs.push({ departureAirport: firstInbound.departureAirport, arrivalAirport: firstInbound.arrivalAirport, departureDate: firstInbound.departureDate })
    }
    return legs
  }, [hasSearched, searchParams, data.segments, outboundOptions, inboundOptions, isReturn])

  const handleSelectFlight = useCallback((selectData: { journey: App.JourneyV2 }) => {
    const currentSegmentKey = activeSegment
    const idx = segments.findIndex(key => key === currentSegmentKey)
    const nextSegmentKey = segments[idx + 1]

    if (idx < segments.length - 1) {
      setSelectedFlights(prev => ({ ...prev, [currentSegmentKey]: selectData.journey }))
      setActiveSegmentKey(nextSegmentKey)
      return
    }

    if (!onSendMessage || isStreaming) return

    const selectedJourneyList = [
      ...segments.slice(0, -1).map(segment => selectedFlights[segment]),
      selectData.journey,
    ].filter((journey): journey is App.JourneyV2 => !!journey)
    const selectedOptionRefs = selectedJourneyList
      .map(journey => initialJourneyOptionRefsById[journey.id])
      .filter((ref): ref is string => !!ref)
    const isCompatibleInitialMultiCitySelection = !isMultiCity ||
      hasSearched ||
      !data.compatibleItineraries?.length ||
      (
        selectedOptionRefs.length === selectedJourneyList.length &&
        data.compatibleItineraries.some(itinerary =>
          areStringArraysEqual(itinerary.optionRefs, selectedOptionRefs),
        )
      )
    if (!isCompatibleInitialMultiCitySelection) return
    const departingJourney = selectedFlights[FlightSegment.DEPARTING]
    const returningJourney = isReturn ? selectData.journey : undefined
    const outbound = selectedJourneyList[0] ?? departingJourney ?? selectData.journey
    const outFlights = outbound.flightGroup.flights
    const from = outFlights[0].departureAirport
    const finalJourney = selectedJourneyList[selectedJourneyList.length - 1] ?? outbound
    const finalFlights = finalJourney.flightGroup.flights
    const to = finalFlights[finalFlights.length - 1].arrivalAirport

    let userMessage: string
    let contextContent: string

    if (isMultiCity) {
      // Backend multi-city pricing is path-dependent, so prefer the priced
      // compatible itinerary's total/currency over re-summing per-leg fares.
      // Fall back to the sum only when no matching itinerary is available.
      const matchingItinerary = selectedOptionRefs.length === selectedJourneyList.length
        ? data.compatibleItineraries?.find(itinerary => areStringArraysEqual(itinerary.optionRefs, selectedOptionRefs))
        : undefined
      const groupCurrency = matchingItinerary?.currencyCode ?? selectedJourneyList[0]?.currencyCode ?? data.currencyCode
      const combinedTotal = matchingItinerary?.totalPrice
        ?? selectedJourneyList.reduce((sum, journey) => sum + (journey.price.adult.totalFare ?? 0), 0)
      const message = buildMultiCityFlightMessage({ intl, from, to, selectedJourneyList, groupCurrency, combinedTotal, buildFlightContext })
      userMessage = message.userMessage
      contextContent = message.contextContent
    } else if (isReturn && departingJourney && returningJourney) {
      userMessage = intl.formatMessage(
        { id: 'ZE1Bw2jDpdHXdHmw312L', defaultMessage: 'Add return flight from {from} to {to}', description: 'User chat message shown when adding a return flight from the flight search drawer' },
        { from, to },
      )
      contextContent = `Departing flight:\n${buildFlightContext(departingJourney)}\n\nReturning flight:\n${buildFlightContext(returningJourney)}`
    } else {
      userMessage = intl.formatMessage(
        { id: 'mtc-4uBZ7D3tkBZV8ueR', defaultMessage: 'Add flight from {from} to {to}', description: 'User chat message shown when adding a one-way flight from the flight search drawer' },
        { from, to },
      )
      contextContent = buildFlightContext(selectData.journey)
    }

    const searchMeta = [
      `Search ID: ${data.searchId}`,
      `Search expiry: ${expiryTimestamp}`,
    ]
    if (hasSearched && searchParams) {
      searchMeta.push(`Search params: ${JSON.stringify(searchParams)}`)
    } else if (data.searchParams) {
      searchMeta.push(`Search params: ${data.searchParams}`)
    }

    const message = `${userMessage}\n<Context label="Flight search result">${contextContent}\n\n${searchMeta.join('\n')}</Context>`
    onSendMessage(message)
  }, [intl, segments, activeSegment, selectedFlights, initialJourneyOptionRefsById, isMultiCity, hasSearched, data.compatibleItineraries, data.currencyCode, data.searchId, data.searchParams, isReturn, onSendMessage, isStreaming, expiryTimestamp, searchParams])

  const flightsHeading = intl.formatMessage({ id: 'Em8QS0fMuOnThg1GQ30N', defaultMessage: 'Flights', description: 'Default heading for the flight search results carousel in the trip planner chat' })

  const showLoading = hasSearched && fetching
  const showError = hasSearched && !fetching && !!error
  const showNoResults = hasSearched && !showLoading && !showError && displayJourneys.length === 0
  const showResults = !showLoading && !showError && displayJourneys.length > 0
  const canSelectFlight = !!onSendMessage

  const drawerOrigin = flights[0]?.departureAirport
  const drawerDestination = isMultiCity ? flights[flights.length - 1]?.arrivalAirport : flights[0]?.arrivalAirport
  const drawerItinerary = useMemo(() => getSearchItinerary(flights, fareType?.value), [flights, fareType?.value])
  // View-all opens the already-searched carousel params. Do not overlay the
  // trip-wide (null-stop) recent bucket — that would replace the chat result
  // the traveller is viewing. Persist from the drawer still uses tripStopId=null.
  const tripStopId = null

  const viewAllLabel = intl.formatMessage({
    id: 'Px4qk3r3kxjxEO_OcsHs',
    defaultMessage: 'View all',
    description: 'Button label to open the full flight search drawer from the chat flight results',
  })
  const canOpenFullResults = FlightSearchDrawerComponent !== undefined

  const isFlightListScrollable = visibleJourneys.length > MAX_VISIBLE_FLIGHT_COUNT
  const visibleFlightCount = Math.min(visibleJourneys.length, MAX_VISIBLE_FLIGHT_COUNT)

  const flightResultsMaxHeight = useMemo(() => {
    if (!showResults || !isFlightListScrollable) return undefined

    const cardHeight = flightCardHeight || FLIGHT_RESULTS_FALLBACK_CARD_HEIGHT
    return (visibleFlightCount * cardHeight) + ((visibleFlightCount - 1) * FLIGHT_RESULTS_GAP) - FLIGHT_RESULTS_SCROLL_PEEK_HEIGHT
  }, [showResults, isFlightListScrollable, flightCardHeight, visibleFlightCount])

  const firstDisplayJourneyId = visibleJourneys[0]?.id

  useEffect(() => {
    if (!showResults) return

    const firstCard = flightResultsListRef.current?.firstElementChild as HTMLDivElement | null
    if (!firstCard) return

    const updateCardHeight = () => {
      setFlightCardHeight(firstCard.getBoundingClientRect().height)
    }

    updateCardHeight()

    if (typeof ResizeObserver === 'undefined') return

    const resizeObserver = new ResizeObserver(updateCardHeight)
    resizeObserver.observe(firstCard)

    return () => {
      resizeObserver.disconnect()
    }
  }, [showResults, visibleJourneys.length, firstDisplayJourneyId])

  return (
    <>
      <SearchWrapper>
        <VerticalSpacer gap={16}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            <Heading variant="heading4">{flightsHeading}</Heading>
            {canOpenFullResults && (
              <TextButton kind="tertiary" size="medium" onClick={openDrawer}>
                {viewAllLabel}
              </TextButton>
            )}
          </Group>
          <FlightSearchWidgetWrapper>
            <FlightSearchWidgetDesktop onSearchAirport={handleSearch} showMultiCity wrapControls />
          </FlightSearchWidgetWrapper>
        </VerticalSpacer>
      </SearchWrapper>

      {(isReturn || isMultiCity) && stepperItinerary.length > 1 && (
        <ContentWrapper>
          <StepperProgressSteps activeStepIndex={currentSegmentIndex}>
            {stepperItinerary.map((leg, index) => (
              <StepperProgressStep
                key={segments[index]}
                stepIndex={index}
                label={intl.formatMessage(
                  { id: 'AEiFGqvrHSc9lXaMfYat', defaultMessage: '{origin} — {destination}', description: 'Flight route step label showing origin and destination airport codes in trip planner' },
                  { origin: leg.departureAirport, destination: leg.arrivalAirport },
                )}
                onStepClick={selectedFlights[segments[index]] ? handleStepClick : undefined}
                sublabel={selectedFlights[segments[index]] ? getStepSublabel(selectedFlights[segments[index]]) : undefined}
              />
            ))}
          </StepperProgressSteps>
        </ContentWrapper>
      )}

      {showLoading && <>
        <FlightsLoadingTopBar />
        <ContentWrapper><LoadingAnimationBox variant="flights" /></ContentWrapper>
      </>}

      {showError && (
        <ContentWrapper>
          <BodyText variant="medium" colour="critical">
            {intl.formatMessage({ id: 'W6e3tDO59GW7SKXKCf3P', defaultMessage: 'Something went wrong while searching for flights. Please try again.', description: 'Error message when flight search fails in the trip planner drawer' })}
          </BodyText>
        </ContentWrapper>
      )}

      {showNoResults && (
        <ContentWrapper>
          <BodyText variant="medium" colour="light-foreground-alt">
            {intl.formatMessage({ id: 'P51jHD0yS8A8bb49PJWq', defaultMessage: 'No flights found for your search. Try different dates or airports.', description: 'Message shown when no flight results are found in the trip planner drawer' })}
          </BodyText>
        </ContentWrapper>
      )}

      {showResults && (
        <ContentWrapper>
          <VerticalSpacer gap={12}>
            <ChatFlightResultsHeader
              label={resultsHeaderLabel}
              count={visibleJourneys.length}
              sort={flightSort}
              originAirportCode={stepperItinerary[currentSegmentIndex]?.departureAirport}
              destinationAirportCode={stepperItinerary[currentSegmentIndex]?.arrivalAirport}
              onSortChange={setFlightSort}
            />

            <TripFlightFiltersPanel
              filterControls={tripFlightFilters}
              originAirportCode={stepperItinerary[currentSegmentIndex]?.departureAirport ?? ''}
              destinationAirportCode={stepperItinerary[currentSegmentIndex]?.arrivalAirport ?? ''}
            />

            {visibleJourneys.length === 0 && (
              <VerticalSpacer gap={12}>
                <BodyText variant="medium" colour="light-foreground-alt">
                  {intl.formatMessage({ id: 'tHI6ugJkg6Xo9BikjkNq', defaultMessage: 'No flights match your filters.', description: 'Message shown when active filters exclude all flight results in the trip planner drawer' })}
                </BodyText>
                <TextButton kind="secondary" size="small" onClick={resetAllFilters}>
                  {intl.formatMessage({ id: 'drpXi_9Ybwre8YQMfj49', defaultMessage: 'Reset filters', description: 'Button label to clear flight filters when no results match in the trip planner drawer' })}
                </TextButton>
              </VerticalSpacer>
            )}

            {visibleJourneys.length > 0 && (
              <FlightResultsViewport
                $maxHeight={flightResultsMaxHeight}
                $isScrollable={isFlightListScrollable}
              >
                <FlightResultsList ref={flightResultsListRef}>
                  {visibleJourneys.map((journey) => (
                    <DrawerFlightCard
                      key={journey.id}
                      journey={journey}
                      onSelect={canSelectFlight ? handleSelectFlight : undefined}
                      onViewDetails={() => showModal(<JourneyV2DetailsModal journey={journey} />)}
                      isAlreadyAdded={false}
                      showSelectButton={canSelectFlight}
                      alignColumns
                      responsiveCompact
                    />
                  ))}
                </FlightResultsList>
              </FlightResultsViewport>
            )}
          </VerticalSpacer>
        </ContentWrapper>
      )}

      {FlightSearchDrawerComponent && (
        <FlightSearchDrawerComponent
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          originAirportCode={drawerOrigin?.airportCode}
          originAirportName={drawerOrigin?.airportName}
          destinationAirportCode={drawerDestination?.airportCode}
          destinationAirportName={drawerDestination?.airportName}
          departureDate={departDate}
          returnDate={returnDate}
          indicativeSearchMonth={itineraryData?.indicativeSearchMonth}
          itinerary={drawerItinerary}
          fareType={fareType?.value}
          fareClass={fareCabin?.value}
          occupants={occupants}
          passengers={occupants?.adults}
          autoSearch
          showMultiCity
          onSendMessage={onSendMessage}
          isStreaming={isStreaming}
          tripStopId={tripStopId}
        />
      )}
    </>
  )
}

export default ChatFlightSearchContent
